import React, { memo, useState, useEffect, useRef, useMemo } from 'react'
import { string, array, func, shape } from 'prop-types'
import classNames from 'classnames'
import { KEYS } from '../../../helpers/constants'
import { useOnClickOutside } from '../../../helpers/hooks/useOnClickOutside'
import { debounce } from '../../../helpers/common'
import Label from './label'

const AutoComplete = memo(({
  label,
  options = [],
  value,
  fieldName,
  titleText,
  errorMessage,
  onValueChange
}) => {
  const [visibleOptions, setVisibleOptions] = useState(options)
  const [userInput, setUserInput] = useState('')
  const [selectedOption, setSelectedOption] = useState(null)
  const [showOptions, setShowOptions] = useState(false)
  const [activeOption, setActiveOption] = useState(0)
  const normalizedOptions = useMemo(() => options
    .map(opt => opt.label.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '')),
  [options])
  const wrapperEl = useRef(null)
  const inputEl = useRef(null)
  const optionsContainerEl = useRef(null)
  const optionsEls = useRef([...Array(options.length)])
  let resultsTimeout
  const closeResults = () => {
    setShowOptions(false)
    clearTimeout(resultsTimeout)
  }
  useOnClickOutside(wrapperEl, closeResults)

  useEffect(() => {
    if (value) {
      setSelectedOption(value)
      setUserInput(value.label || '')
    }
  }, [value])

  useEffect(() => {
    if (selectedOption &&
        !options.find(opt => opt.value === selectedOption.value)) {
      clearInput()
    }
  }, [options])

  const debouncedOnChange = debounce(onValueChange, 300)
  const filterOptions = val => {
    const normalizedVal = val.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '')
    return options.filter((_, idx) => {
      return normalizedOptions[idx].indexOf(normalizedVal) > -1
    })
  }

  const onChange = e => {
    const userInput = e.currentTarget.value.toLowerCase()
    if (!userInput.length) openResults(null, userInput)
    setVisibleOptions(filterOptions(userInput))
    setUserInput(userInput)
    debouncedOnChange(selectedOption)
  }

  const onClick = e => {
    debouncedOnChange(selectedOption)
    closeResults()
  }

  const onMouseEnter = (idx, option) => {
    setActiveOption(idx)
    setUserInput(option.label)
    setSelectedOption(option)
  }

  const scrollOptions = option => {
    const { clientHeight: containerHeight, scrollTop: limitTop } = optionsContainerEl.current
    const limitBottom = limitTop + containerHeight
    const { clientHeight: optionHeight, offsetTop: optionTop } = option
    const optionBottom = optionTop + optionHeight
    if (optionTop <= limitTop || optionBottom >= limitBottom) {
      const scrollDistance = optionBottom > limitBottom ? limitTop + containerHeight : optionBottom - containerHeight
      optionsContainerEl.current.scroll({ top: scrollDistance, left: 0, behavior: 'smooth' })
    }
  }

  const onKeyDown = e => {
    if (e.keyCode === KEYS.ENTER) {
      e.preventDefault()
      if (!selectedOption.label.length) return
      onValueChange(selectedOption)
      closeResults()
    } else if (e.keyCode === KEYS.ESCAPE) {
      closeResults()
    } else if (e.keyCode === KEYS.UP || e.keyCode === KEYS.DOWN) {
      let newActiveIndex
      if (e.keyCode === KEYS.UP) {
        if (activeOption === 0) return
        newActiveIndex = activeOption - 1
      } else if (e.keyCode === KEYS.DOWN) {
        newActiveIndex = activeOption + 1
        if (newActiveIndex === visibleOptions.length) return
      }
      setSelectedOption(visibleOptions[newActiveIndex])
      setUserInput(visibleOptions[newActiveIndex].label)
      setActiveOption(newActiveIndex)
      scrollOptions(optionsEls.current[newActiveIndex])
    }
  }

  const openResults = (e, value) => {
    setVisibleOptions(value ? filterOptions(value) : options)
    setShowOptions(true)
  }

  const clearInput = openResultsAfterClear => {
    setUserInput('')
    setSelectedOption(null)
    onValueChange({})
    if (openResultsAfterClear) openResults()
  }

  const setOptionClasses = idx => classNames('nft-autocomplete__item', { 'selected': activeOption === idx })

  const optionTags = visibleOptions.map((option, idx) =>
    <span
      ref={el => { optionsEls.current[idx] = el }}
      key={`opt-${idx}`}
      className={setOptionClasses(idx)}
      onClick={onClick}
      onMouseEnter={() => onMouseEnter(idx, option)}>{option.label}</span>)

  const containerClasses = classNames('nft-field', 'nft-autocomplete', { 'has-error': !!errorMessage })
  return (
    <div className={containerClasses}>
      { label && <Label text={label} labelFor={`user-${fieldName}`} />}
      <div ref={wrapperEl}>
        { userInput.length > 0 && options.length > 1 &&
          <button className='nft-autocomplete__clear' type='button' onClick={() => clearInput(true)}>×</button>}
        <input
          className='afd-textfield__input'
          id={`user-${fieldName}`}
          name={`user-${fieldName}`}
          autoComplete='none'
          ref={inputEl}
          value={userInput}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={openResults}
          onBlur={() => { resultsTimeout = setTimeout(closeResults, 200) }}
          disabled={!options.length}
          data-insights-funnel-step={fieldName}
          title={titleText} />
        {errorMessage && <p className='nft-error-msg'>{errorMessage}</p>}
        {showOptions && <div className='nft-autocomplete__options' tabIndex='0' ref={optionsContainerEl}>{optionTags}</div>}
      </div>
    </div>
  )
})

AutoComplete.propTypes = {
  label: string,
  options: array.isRequired,
  fieldName: string.isRequired,
  value: shape({ label: string, value: string }),
  errorMessage: string,
  onValueChange: func.isRequired,
  titleText: string
}

AutoComplete.displayName = 'AutoComplete';

export default AutoComplete